<template>
  <div class="form-group">
    <label :for="name" :style="{ visibility: showLabel ? 'visible' : 'hidden' }">
      <div style="display:flex;flex-direction: column;align-items: start">
        <span>
          {{ label }} 
          <span v-if="required" class="required-asterisk">*</span>
        </span>
        <span style="font-size: 9px;color:red;font-weight:600;" v-if="light_label">&nbsp; {{light_label}}</span>
      </div>
    </label>
    <div class="input-wrapper">
      <!-- <q-input v-model="password" filled :type="isPwd ? 'password' : 'text'" hint="Password with toggle">
        <template v-slot:append>
          <q-icon
            :name="isPwd ? 'visibility_off' : 'visibility'"
            class="cursor-pointer"
            @click="isPwd = !isPwd"
          />
        </template>
      </q-input> -->
      <input
        :id="name"
        :style="{ height: customHeight ? customHeight + 'px' : 'auto' }"
        :type="currentInputType"
        :placeholder="placeholder"
        v-model="displayedValue"
        autocomplete="off"
        :readonly="isReadonly" 
        @focus="isReadonly = false"
        @focusin="isReadonly = false"
        @blur="validateInput"
        @input="validateInput"
        @change="validateInput"
        @keypress="restrictInput"
        :max="maxdate"
        :min="mindate"
        :minlength="minlength"
        :maxlength="maxlength"
        :disabled="disabled"
        :success="success"
        :infoMessage="infoMessage"
        :successMessage="successMessage"
        :successIconOnEnd="successIconOnEnd"
        :class="{ 'is-invalid': errorMessage || failedMessage, 'is-success': !!success }"
      />
      <img
        v-if="successIconOnEnd"
        src="~@/assets/common/images/icons/verified.svg"
        class="success-right-icon"
        width="25"
        alt="Verified"
      />
    </div>
    <span v-if="errorMessage && !failedMessage" class="error-message">{{ errorMessage }}</span>
    <span v-if="failedMessage && !success" class="failed-message"> &nbsp;{{ failedMessage }}</span>
    <span v-if="successMessage && success" class="success-message">
      <img src="~@/assets/common/images/icons/verified.svg" width="20" v-if="showSuccessIcon"/> &nbsp;{{ successMessage }}
    </span>
    <span v-if="infoMessage && !errorMessage" style="font-weight:700;font-size:16px;display:flex;justify-content: left;">&nbsp;{{ infoMessage }}</span>
  </div>
</template>

<script>
import { ref, watch } from 'vue';

export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    showLabel: {
      type: Boolean,
      required: false,
      default:true
    },
    successIconOnEnd: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    onlyCapitals:{
      type:Boolean,
      required:false,
    },
    minlength: {
      type: String,
      required: false,
    },
    maxlength: {
      type: String,
      required: false,
    },
    light_label: {
      type: String,
      required: false,
      default: '',
    },
    maxdate: {
      type: String,
      required: false,
    },
    mindate: {
      type: String,
      required: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    customHeight: {
      type: Number,
      required:false
    },
    successMessage: {
      type: String,
      required: false,
    },
    infoMessage: {
      type: String,
      required: false,
    },
    failedMessage: {
      type: String,
      required: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
    showSuccessIcon: {
      type: Boolean,
      default: false,
    },
    validationType: {
      type: String,
      default: 'both', // 'both', 'no-numbers', 'numbers-only'
    },
    customRules: {
      type: Function,
      default: null,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const inputValue = ref(props.modelValue);
    const displayedValue = ref(props.modelValue);
    const errorMessage = ref('');
    const currentInputType = ref(props.type); // Initialize with prop type
    let timeoutId = null;

    const validateInput = (event) => {
      const inputValue = event.target?.value || displayedValue.value;
      displayedValue.value = inputValue;
      // Handle password-specific behavior
      if (props.type === 'password') {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          currentInputType.value = 'password'; // Switch to password type
          emit('update:modelValue', inputValue); // Emit the updated value
        }, 500);

        currentInputType.value = 'text'; // Display text immediately
      } else {
        emit('update:modelValue', inputValue); // For non-password fields
      }

      // Perform validation
      errorMessage.value = '';

      if (props.required && !inputValue) {
        errorMessage.value = 'Please Enter '+props.label;
      } else if (props.minlength && inputValue.length < props.minlength && inputValue.length!=0) {
        errorMessage.value = `Please enter ${props.minlength} digit ${props.label}`;
      } else if (props.maxlength && inputValue.length > props.maxlength) {
        errorMessage.value = `Maximum length is ${props.maxlength} characters`;
      } else if (props.validationType === 'no-numbers' && /\d/.test(inputValue)) {
        errorMessage.value = 'Numbers are not allowed';
      } else if (props.validationType === 'numbers-only' && /\D/.test(inputValue)) {
        errorMessage.value = 'Only numbers are allowed';
      }

      if (props.customRules) {
        const customError = props.customRules(inputValue);
        if (customError) {
          errorMessage.value = customError;
        }
      }
    };
    const conditionalInputHandler = (event) => {
      if (props.type === 'password') {
        handleInput(event);
      } else {
        validateInput();
        emit('update:modelValue', event.target.value);
      }
    };
    const handleInput = (event) => {
      const inputValue = event.target.value;
      displayedValue.value = inputValue;

      if (props.type === 'password') {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          currentInputType.value = 'password'; // Switch to password type
          emit('update:modelValue', inputValue); // Emit the updated value
        }, 500);

        currentInputType.value = 'text'; // Display text immediately
      } else {
        emit('update:modelValue', inputValue); // For non-password fields
      }
    };

    const restrictInput = (event) => {
      const char = String.fromCharCode(event.charCode);
      const isSpecialChar = /[!@#$%^&*()`~,.'/\-?":{}|<>=_+;[\]]/.test(char); // Check for all special characters

      if (props.validationType === 'no-numbers' && /\d/.test(char) ) {
        // Prevent special characters in any case
        if (isSpecialChar) {
          event.preventDefault();
        }
        event.preventDefault();
      } else if (props.validationType === 'numbers-only' && /\D/.test(char) ) {
        if (isSpecialChar) {
          event.preventDefault();
        }
        event.preventDefault();
      }
      // else{
      //   event.preventDefault();
      // }
    };

    watch(inputValue.value, (newValue) => {
      displayedValue.value = newValue;
    });
    watch(() => props.modelValue, (newValue) => {
      if (props.onlyCapitals) {
        displayedValue.value = newValue.toUpperCase(); // Update to uppercase if onlyCapitals is true
      } else {
        // alert(newValue);
        inputValue.value = newValue;
        displayedValue.value = newValue;
        validateInput(inputValue.value);
      }
    });
    // watch(() => props.modelValue, (newValue) => {
    //   inputValue.value = newValue;
    //   displayedValue.value = newValue;
    // });

    watch(() => props.type, (newType) => {
      currentInputType.value = newType;
      if (newType === 'password') {
        currentInputType.value = 'text'; // Default to text for initial display
      }
    });
    const isReadonly= ref(true);
    return {
      isReadonly,
      inputValue,
      displayedValue,
      currentInputType,
      errorMessage,
      validateInput,
      handleInput,
      conditionalInputHandler,
      restrictInput,
    };
  },
};
</script>

<style scoped>
.input-wrapper {
  position: relative;
}
.success-right-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
input[type=text], input[type=date], input[type=email], input[type=number], input[type=password] {
  position: relative;
  width: 100%;
  height: 60px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: .75rem;
  color: #333;
  outline: none;
  box-shadow: none;
  font-size: 16px;
  letter-spacing: 1px;
}
input[type=password] {
  -webkit-text-security: disc;
  text-security: disc;
}
@supports (-webkit-appearance: none) {
  input[type="date"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }
}
.form-group {
  margin-bottom: 20px;
}
label {
  display: flex;
  align-items: center;
  font-weight: bold !important;
  font-size: 16px !important;
}
.required-asterisk {
  color: red;
  margin-left: 5px;
}
input {
  width: 100%;
  padding: 10px;
  border: 2px solid transparent;
  border-radius: 4px;
  margin-top: 5px;
  box-sizing: border-box;
  font-family: Montserrat Regular, sans-serif;
}
input.is-success {
  border-color: #83eb5a;
}
input.is-info {
  border-color: #006ea5;
}
.success-message {
  color: #83eb5a;
  font-size: 0.875em;
  margin-top: 5px;
  display: flex;
}
.failed-message {
  color: #eb5a5a;
  font-size: 0.875em;
  margin-top: 5px;
  display: flex;
}
input.is-invalid {
  border-color: #eb5a5a;
}
.error-message {
  text-align: left;
  color: red;
  font-size: 0.875em;
  margin-top: 5px;
  display: block;
}
</style>
